import React, { useCallback } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Stack, xcss } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';

import { BulkCreateBody } from '../bulk-work-create-list/BulkCreateBody';
import { BulkCreateHeader } from '../bulk-work-create-common/BulkCreateHeader';
import { BulkCreateFooter } from '../bulk-work-create-common/BulkCreateFooter';
import { SidePanelWrapper } from '../work-create-common/SidePanelWrapper';
import {
	BULK_ISSUE_CREATE_SOURCE,
	useBulkCreateLoggingEvents,
} from '../../utils/useBulkCreateLoggingEvents';
import {
	useBulkCreateContext,
	ErrorMessageMap,
	ErrorStates,
	AiStates,
} from '../../providers/BulkCreateContextProvider';
import { useGetIssueInsertionActions } from '../../services/useGetIssueInsertionActions';
import { WorkItemCreationError } from '../work-create-common';

import { IssueCreateAiLoading } from './IssueCreateAiLoading';
import { RainbowLoadingSidebar } from './RainbowLoadingSidebar';

const contentStyles = xcss({
	width: '100%',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	flexWrap: 'nowrap',
});

export const AiBulkDefaultView = ({ onClose }: { onClose: () => void }) => {
	const { logCancelAiPanel } = useBulkCreateLoggingEvents();
	const { addSingleIssuesToPage } = useGetIssueInsertionActions();
	const [{ aiState, error }, actions] = useBulkCreateContext();

	const onPanelClose = useCallback(() => {
		logCancelAiPanel();
		addSingleIssuesToPage();
		if (fg('confluence-create-work-items-from-action-items')) {
			void actions.resetTransientBulkCreateState();
		} else {
			void actions.resetAIBulkCreateState();
		}
		onClose();
	}, [logCancelAiPanel, onClose, addSingleIssuesToPage, actions]);

	const isLoading = aiState === AiStates.LOADING;
	const isError = error !== ErrorStates.EMPTY;
	const isComplete = !isLoading && !isError;

	return (
		<SidePanelWrapper>
			<RainbowLoadingSidebar isShown={isLoading}>
				<Stack xcss={contentStyles}>
					<BulkCreateHeader onClose={onPanelClose} />
					{isLoading && <IssueCreateAiLoading loadingType="bulk" />}
					{isError && (
						<WorkItemCreationError
							creationSource={BULK_ISSUE_CREATE_SOURCE}
							errorMessage={ErrorMessageMap[error]()}
							isCreatedWithAi
						/>
					)}
					{isComplete && (
						<>
							<BulkCreateBody />
							<BulkCreateFooter onClose={onPanelClose} onCreateAll={onClose} />
						</>
					)}
				</Stack>
			</RainbowLoadingSidebar>
		</SidePanelWrapper>
	);
};
